import {
  EbayListingStatus,
  EbayProductFieldType,
  EbayVariantFieldType,
} from "@redotech/redo-prisma-client/enums";
import { assertNever } from "@redotech/util/type";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import {
  attributeDefinitionIdFormat,
  ebayListingIdFormat,
  imageIdFormat,
} from "../ids";
import {
  AttributeType,
  AttributeValueSchema,
  AttributeValueType,
} from "../inventory/attributes";
import { RedoListingStatus } from "../inventory/redo-listing-status";

/** eBay imposes an 80-character maximum for listing titles */
export const EBAY_TITLE_MAX_LENGTH = 80;

export const EbayProductStatusSchema = createEnum(EbayListingStatus);

export const EbayListingIdWithStatusSchema = z.object({
  id: ebayListingIdFormat.schema,
  status: EbayProductStatusSchema,
});
export type EbayListingIdWithStatus = z.infer<
  typeof EbayListingIdWithStatusSchema
>;

export function convertEbayListingStatusToRedoListingStatus(
  status: EbayListingStatus,
): RedoListingStatus {
  switch (status) {
    case EbayListingStatus.ACTIVE:
      return RedoListingStatus.ACTIVE;
    case EbayListingStatus.DRAFT:
      return RedoListingStatus.DRAFT;
    case EbayListingStatus.ARCHIVED:
      return RedoListingStatus.ARCHIVED;
    default:
      assertNever(status);
  }
}

const EbayListingAspectSchema_ = z.object({
  id: z.string(),
  name: z.string(),
  value: z.string(),
});
export const EbayListingAspectSchema: z.ZodType<EbayListingAspect> =
  EbayListingAspectSchema_;
export interface EbayListingAspect
  extends z.infer<typeof EbayListingAspectSchema_> {}

export const EbayListingSchema_ = z.object({
  channel: z.literal("EBAY"),
  id: z.string(),
  productId: z.string(),
  catalogId: z.string().nullable(),
  sku: z.string().nullable(),
  marketplaceId: z.string().nullable(),
  condition: z.string().nullable(),
  format: z.string().nullable(),
  title: z.string().nullable(),
  imageUrls: z.array(z.string()),
  productDescription: z.string().nullable(),
  availableQuantity: z.number().nullable(),
  categoryId: z.string().nullable(),
  listingDescription: z.string().nullable(),
  listingDuration: z.string().nullable(),
  listingStartDate: z.date().nullable(),
  price: z.number().nullable(),
  fulfillmentPolicyId: z.string().nullable(),
  paymentPolicyId: z.string().nullable(),
  returnPolicyId: z.string().nullable(),
  merchantLocationKey: z.string().nullable(),
  vatPercentage: z.number().nullable(),
  applyTax: z.boolean().nullable(),
  ebayExternalId: z.string().nullable(),
  createdAt: z.date(),
  updatedAt: z.date(),
  aspects: z.array(EbayListingAspectSchema),
});
export const EbayListingSchema: z.ZodType<EbayListing> = EbayListingSchema_;
export interface EbayListing extends z.infer<typeof EbayListingSchema_> {}

export interface EbayUpdateListingInput {
  accountId: bigint;
  listingId: bigint;
  title?: string;
  productDescription?: string;
  listingDescription?: string;
  price?: number;
  sku?: string;
  condition?: string;
  categoryId?: string;
  availableQuantity?: number;
  aspects?: Record<string, string[]>;
}

const CalculatedEbayListingDataSchema_ = z.object({
  sku: z.string(),
  title: z.string().max(EBAY_TITLE_MAX_LENGTH),
  productDescription: z.string(),
  listingDescription: z.string().nullable(),
  imageUrls: z.array(z.string()),
  imageIds: z.array(imageIdFormat.schema.nullable()).nullish(),
  availableQuantity: z.number(),
  price: z.bigint().nullable(),
  categoryId: z.string().nullable(),
  condition: z.string().nullable(),
  format: z.string().nullable(),
  marketplaceId: z.string().nullable(),
  listingDuration: z.string().nullable(),
  merchantLocationKey: z.string().nullable(),
  fulfillmentPolicyId: z.string().nullable(),
  paymentPolicyId: z.string().nullable(),
  returnPolicyId: z.string().nullable(),
  aspects: z.record(z.string(), z.array(z.string())),
});
export const CalculatedEbayListingDataSchema: z.ZodType<CalculatedEbayListingData> =
  CalculatedEbayListingDataSchema_;
export interface CalculatedEbayListingData
  extends z.infer<typeof CalculatedEbayListingDataSchema_> {}

export const EbayProductAttributeType = {
  [EbayProductFieldType.title]: AttributeType.ShortText,
  [EbayProductFieldType.description]: AttributeType.LongText,
  [EbayProductFieldType.condition]: AttributeType.ShortText,
  [EbayProductFieldType.price]: AttributeType.Money,
  [EbayProductFieldType.brand]: AttributeType.ShortText,
  [EbayProductFieldType.category]: AttributeType.ShortText,
  [EbayProductFieldType.aspect]: AttributeType.ShortText,
} satisfies Record<EbayProductFieldType, AttributeType>;

export const EbayProductAttributeSchema = {
  [EbayProductFieldType.title]:
    AttributeValueSchema[
      EbayProductAttributeType[EbayProductFieldType.title]
    ].nullable(),
  [EbayProductFieldType.description]:
    AttributeValueSchema[
      EbayProductAttributeType[EbayProductFieldType.description]
    ].nullable(),
  [EbayProductFieldType.condition]:
    AttributeValueSchema[
      EbayProductAttributeType[EbayProductFieldType.condition]
    ].nullable(),
  [EbayProductFieldType.price]:
    AttributeValueSchema[
      EbayProductAttributeType[EbayProductFieldType.price]
    ].nullable(),
  [EbayProductFieldType.brand]:
    AttributeValueSchema[
      EbayProductAttributeType[EbayProductFieldType.brand]
    ].nullable(),
  [EbayProductFieldType.category]:
    AttributeValueSchema[
      EbayProductAttributeType[EbayProductFieldType.category]
    ].nullable(),
  [EbayProductFieldType.aspect]:
    AttributeValueSchema[
      EbayProductAttributeType[EbayProductFieldType.aspect]
    ].nullable(),
};

export type EbayProductAttributeValueType = {
  [key in EbayProductFieldType]: (typeof EbayProductAttributeSchema)[key] extends z.ZodType<
    infer T
  >
    ? T
    : never;
};

export function getEbayProductAttributeValue<T extends EbayProductFieldType>(
  field: T,
  value: unknown,
): EbayProductAttributeValueType[T] | null {
  return EbayProductAttributeSchema[field].parse(
    value,
  ) as EbayProductAttributeValueType[T];
}

export function getEbayProductAttributeType<T extends EbayProductFieldType>(
  field: T,
): (typeof EbayProductAttributeType)[T] {
  return EbayProductAttributeType[field];
}

export const EbayVariantAttributeType = {
  [EbayVariantFieldType.sku]: AttributeType.ShortText,
  [EbayVariantFieldType.price]: AttributeType.Money,
  [EbayVariantFieldType.aspect]: AttributeType.ShortText,
} satisfies Record<EbayVariantFieldType, AttributeType>;

export const EbayVariantAttributeSchema = {
  [EbayVariantFieldType.sku]:
    AttributeValueSchema[
      EbayVariantAttributeType[EbayVariantFieldType.sku]
    ].nullable(),
  [EbayVariantFieldType.price]:
    AttributeValueSchema[
      EbayVariantAttributeType[EbayVariantFieldType.price]
    ].nullable(),
  [EbayVariantFieldType.aspect]:
    AttributeValueSchema[
      EbayVariantAttributeType[EbayVariantFieldType.aspect]
    ].nullable(),
};

export type EbayVariantAttributeValueType = {
  [key in EbayVariantFieldType]: (typeof EbayVariantAttributeSchema)[key] extends z.ZodType<
    infer T
  >
    ? T
    : never;
};

export function getEbayVariantAttributeType<T extends EbayVariantFieldType>(
  field: T,
): (typeof EbayVariantAttributeType)[T] {
  return EbayVariantAttributeType[field];
}

export const EbayProductFieldTypeSchema = createEnum(EbayProductFieldType);
export const EbayVariantFieldTypeSchema = createEnum(EbayVariantFieldType);

const EbayProductMappingSchema_ = z.object({
  fieldName: EbayProductFieldTypeSchema,
  aspectName: z.string(),
  attributeDefinitionId: attributeDefinitionIdFormat.schema,
});
export const EbayProductMappingSchema: z.ZodType<EbayProductMapping> =
  EbayProductMappingSchema_;
export interface EbayProductMapping
  extends z.infer<typeof EbayProductMappingSchema_> {}

const EbayVariantMappingSchema_ = z.object({
  fieldName: EbayVariantFieldTypeSchema,
  aspectName: z.string(),
  attributeDefinitionId: attributeDefinitionIdFormat.schema,
});
export const EbayVariantMappingSchema: z.ZodType<EbayVariantMapping> =
  EbayVariantMappingSchema_;
export interface EbayVariantMapping
  extends z.infer<typeof EbayVariantMappingSchema_> {}

const EbayMappingSchema_ = z.object({
  productMappings: z.array(EbayProductMappingSchema).readonly(),
  variantMappings: z.array(EbayVariantMappingSchema).readonly(),
});
export const EbayMappingSchema: z.ZodType<EbayMapping> = EbayMappingSchema_;
export interface EbayMapping extends z.infer<typeof EbayMappingSchema_> {}

export function getEbayListingAttributeValue<T extends EbayProductFieldType>({
  listing,
  field,
  aspectName,
}: {
  listing: CalculatedEbayListingData;
  field: T;
  aspectName?: string;
}): AttributeValueType[AttributeType] | null {
  let fieldValue;
  switch (field) {
    case "title":
      fieldValue = listing.title;
      break;
    case "description":
      fieldValue = listing.productDescription;
      break;
    case "condition":
      fieldValue = listing.condition;
      break;
    case "price":
      fieldValue =
        listing.price != null
          ? { amount: listing.price, currency: "USD" }
          : null;
      break;
    case "brand":
      fieldValue = listing.aspects.brand ?? null;
      break;
    case "category":
      fieldValue = listing.categoryId;
      break;
    case "aspect":
      fieldValue = aspectName ? (listing.aspects[aspectName] ?? null) : null;
      break;
    default:
      assertNever(field);
  }

  return EbayProductAttributeSchema[field].parse(fieldValue);
}
